import { LocationStrategy } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { Compiler, ErrorHandler, InjectionToken, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  AuthService,
  BreadCrumbService,
  ENVIRONMENT_LOADED_HANDLER_TOKEN,
  ENVIRONMENT_PROVIDER_TOKEN,
  FrameworkNgxModule,
  HttpErrorInterceptor,
  HttpLanguageInterceptor,
  IBaseEnvironment,
  IEnvironmentProvider,
  Navigator,
  StatePathLocationStrategy,
  AppErrorhandler,
} from '@kpd/framework-ngx';
import { ActionReducerMap, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { OAuthModule, OAuthModuleConfig } from 'angular-oauth2-oidc';
import { AppComponent } from 'app/app.component';
import { AppRoutingModule } from 'app/app-routing.module';
import { LicensesModule } from 'app/licenses/licenses.module';
import { PermissionsetsModule } from 'app/permissionsets/permissionsets.module';
import { RolesModule } from 'app/roles/roles.module';
import { SharedModule } from 'app/shared/shared.module';
import { getReducers, IAppState } from 'app/store';
import { UsersModule } from 'app/users/users.module';
import { environment } from 'environments/environment';
import * as log from 'loglevel';
import { MessageService } from 'primeng/components/common/messageservice';
import { GrowlModule } from 'primeng/growl';
import { IApplicationEnvironment } from 'environments/application-environment.interface';
import { PermissionsModule } from './permissions/permissions.module';

export const REDUCER_TOKEN = new InjectionToken<ActionReducerMap<IAppState>>('Registered Reducers');

// Translate loader
export function createTranslateLoader(httpClient: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(httpClient);
}

export class EnvironmentProvider implements IEnvironmentProvider {
  get environment(): IBaseEnvironment {
    return environment;
  }
}

const oAuthModuleConfig: OAuthModuleConfig = {
  resourceServer: {
    allowedUrls: [], // will be updated after the load of the env.json file
    sendAccessToken: true,
  },
};

export function AuthConfigFactory(): OAuthModuleConfig {
  return oAuthModuleConfig;
}

export function EnvironmentLoadedHandler(_env: IApplicationEnvironment): void {
  // Update the allowedUrls in the auth config when the env.json file is loaded
  oAuthModuleConfig.resourceServer.allowedUrls = _env.allowedUrls;
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserAnimationsModule,
    FormsModule,
    FrameworkNgxModule,
    HttpClientModule,
    GrowlModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    SharedModule,
    UsersModule,
    LicensesModule,
    RolesModule,
    PermissionsetsModule,
    PermissionsModule,
    StoreModule.forRoot(REDUCER_TOKEN),
    OAuthModule.forRoot(),
    StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: environment.production }),
    AppRoutingModule, // This must be imported last, since the 404 route will otherwise overwrite other routes
  ],
  providers: [
    // Services
    AuthService,

    MessageService,
    // Global Error Handler
    {
      provide: ErrorHandler,
      useClass: AppErrorhandler,
    },
    // Http Interceptors
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpLanguageInterceptor,
      multi: true,
    },
    {
      provide: REDUCER_TOKEN,
      useFactory: getReducers,
    },
    Navigator,
    BreadCrumbService,
    {
      provide: LocationStrategy,
      useClass: StatePathLocationStrategy,
    },
    {
      provide: REDUCER_TOKEN,
      useFactory: getReducers,
    },
    {
      provide: ENVIRONMENT_PROVIDER_TOKEN,
      useClass: EnvironmentProvider,
    },
    {
      provide: OAuthModuleConfig,
      useFactory: AuthConfigFactory,
    },
    {
      provide: ENVIRONMENT_LOADED_HANDLER_TOKEN,
      useValue: EnvironmentLoadedHandler,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(compiler: Compiler) {
    log.setDefaultLevel(environment.logLevel);
    log.setLevel(environment.logLevel);
    log.debug('Started');
    log.debug(`App in ${compiler instanceof Compiler ? 'AOT' : 'JIT'} mode`);
  }
}
